import type { ContentFormat } from '@/lib/contentFormat';

/** File extensions for each content format's raw markup source. */
export const CONTENT_FORMAT_EXTENSIONS: Record<ContentFormat, string> = {
  djot: 'djot',
  asciidoc: 'adoc',
  markdown: 'md',
  plaintext: 'txt',
};

/**
 * Turns a document title into a safe, portable filename stem:
 * kebab-cased ASCII, ~60 chars max, `'wikistar-export'` as a last resort.
 */
export function slugifyFilename(title: string): string {
  const slug = title
    .toLowerCase()
    .normalize('NFKD')
    // Strip the combining diacritical marks NFKD separates out (U+0300–U+036F).
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  return slug || 'wikistar-export';
}

/** Escapes a string for interpolation into an HTML text/attribute context. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Triggers a browser download of `content` as `filename`. Uses a Blob
 * object URL rather than a `data:` URI — data URIs hit length limits on
 * large documents and some browsers refuse to navigate to them.
 */
export function downloadTextFile(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke lazily so the download has time to start before the URL dies.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Rewrites root-relative links (`/w/...`, `/npub1...`) in rendered export
 * HTML into absolute links back to this app instance, so wikilinks and
 * nostr: links in a downloaded file still open somewhere meaningful
 * instead of resolving to `file:///w/...`. Link hrefs reach this already
 * React-escaped (only `sanitizeUrl`-approved https URLs and internal
 * paths ever get rendered), so a plain string replace is safe here.
 */
export function absolutizeInternalLinks(html: string, origin: string): string {
  return html.replaceAll('href="/', `href="${origin}/`);
}

/**
 * The embedded stylesheet for exported standalone HTML. Deliberately
 * styles bare elements (not Tailwind classes — the utility framework
 * doesn't exist in a downloaded file, but the renderers emit semantic
 * markup that element selectors cover well). Includes a dark variant so
 * the file stays readable in a dark-mode browser or e-reader.
 */
const EXPORT_CSS = `
:root { color-scheme: light dark; }
body { font-family: Georgia, 'Times New Roman', serif; font-size: 17px; line-height: 1.7; max-width: 42rem; margin: 0 auto; padding: 2.5rem 1.25rem; color: #1c1c1e; background: #fff; overflow-wrap: break-word; }
h1, h2, h3, h4, h5, h6 { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.3; margin: 1.7em 0 0.6em; }
h1 { font-size: 1.9em; } h2 { font-size: 1.5em; border-bottom: 1px solid #e4e4e7; padding-bottom: 0.3em; } h3 { font-size: 1.25em; } h4 { font-size: 1.1em; }
p { margin: 0 0 1em; }
a { color: #2563eb; text-decoration: underline; text-underline-offset: 2px; }
img { max-width: 100%; height: auto; border-radius: 8px; display: block; margin: 1.5em auto; }
pre { background: #f4f4f5; padding: 1rem; border-radius: 8px; overflow-x: auto; font-size: 0.85em; }
code { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.88em; background: #f4f4f5; padding: 0.15em 0.35em; border-radius: 4px; }
pre code { background: none; padding: 0; }
blockquote { border-left: 4px solid #d4d4d8; margin: 0 0 1em; padding-left: 1rem; color: #52525b; font-style: italic; }
ul, ol { margin: 0 0 1em; padding-left: 1.5em; }
dl { margin: 0 0 1em; } dt { font-weight: 600; } dd { padding-left: 1rem; color: #52525b; margin: 0 0 0.5em; }
table { border-collapse: collapse; width: 100%; margin: 0 0 1em; font-size: 0.9em; }
th, td { border-bottom: 1px solid #e4e4e7; padding: 0.5rem; text-align: left; }
hr { border: none; border-top: 1px solid #e4e4e7; margin: 2rem 0; }
mark { background: #fef08a; padding: 0 2px; border-radius: 2px; }
ins { text-decoration: underline; } del { text-decoration: line-through; }
.export-header { margin-bottom: 2rem; }
.export-header h1 { margin: 0 0 0.4em; }
.export-byline, .export-footer { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 0.82em; color: #71717a; }
.export-byline .export-pub-title { font-style: italic; }
.export-footer { margin-top: 3rem; padding-top: 1rem; border-top: 1px solid #e4e4e7; }
.export-toc { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0 0 2.5rem; padding: 1rem 1.25rem; background: #fafafa; border: 1px solid #e4e4e7; border-radius: 8px; }
.export-toc h2 { margin: 0 0 0.5rem; font-size: 0.95em; border: none; padding: 0; }
.export-toc ol { margin: 0; padding-left: 1.4em; }
.export-toc li { margin: 0.15em 0; }
.export-toc a { color: inherit; text-decoration: none; }
.export-toc a:hover { text-decoration: underline; }
.export-section { margin-top: 2.5rem; }
.export-nested-note { color: #71717a; font-style: italic; }
@media (prefers-color-scheme: dark) {
  body { background: #141416; color: #e4e4e7; }
  h2 { border-bottom-color: #3f3f46; }
  a { color: #60a5fa; }
  pre, code { background: #27272a; }
  pre code { background: none; }
  blockquote { border-left-color: #3f3f46; color: #a1a1aa; }
  dd { color: #a1a1aa; }
  th, td, hr { border-color: #3f3f46; }
  mark { background: #713f12; }
  .export-byline, .export-footer, .export-nested-note { color: #a1a1aa; }
  .export-footer { border-top-color: #3f3f46; }
  .export-toc { background: #1c1c1f; border-color: #3f3f46; }
}
`;

interface StandaloneHtmlOptions {
  /** Document title — interpolated into `<title>`, so it is HTML-escaped here. */
  title: string;
  /** Optional `<meta name="description">`. Also escaped. */
  description?: string;
  /**
   * The page body — MUST be renderer-produced HTML (React-escaped,
   * URL-sanitized), never raw event content or tag values.
   */
  bodyHtml: string;
}

/** Wraps rendered body HTML in a complete, self-contained HTML document. */
export function buildStandaloneHtml({ title, description, bodyHtml }: StandaloneHtmlOptions): string {
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
${description ? `<meta name="description" content="${escapeHtml(description)}">\n` : ''}<style>${EXPORT_CSS}</style>
</head>
<body>
${bodyHtml}
</body>
</html>
`;
}
